import { useMemo, useRef } from "react";
import { QueryKey, useQuery, UseQueryOptions } from "react-query";
import { fetchAntePoolsFromAPI, FetchAntePoolsParams } from "api/antepools";
import { Pool } from "types";
import queryClient from "utils/queryClient";
import { PoolsCache } from "utils/antepools/cache";
import { fetchOnChainPoolsData } from "utils/antepools/fetchAntePoolsPublic";
import { formatPools } from "utils/antepools/utils";
import { useWallet, Wallet } from "utils/wallet";

export type GetAntePoolsQueryVariables = FetchAntePoolsParams;

/**
 * Level 2 nested field filters must be custom implemented
 *
 * @param data
 * @param filters
 * @returns
 */
export const handlePoolFilters = <T>(
  data: Array<T>,
  filters: Record<string, any>
): Array<T> => {
  let filteredData = data;

  Object.keys(filters).forEach((field) => {
    if (filters[field] === undefined) return;

    filteredData = filteredData.filter((obj) => {
      if (field === "writerAddress") {
        return obj?.["testAuthor"]?.["address"] === filters[field];
      } else if (field === "address") {
        return obj?.["antePoolAddress"] === filters[field];
      }

      return obj[field] === filters[field];
    });
  });

  filteredData = filteredData.filter((obj) => {
    return (obj as any)?.isHidden !== true;
  });
  return filteredData;
};

export const getCachedPools = (walletData?: Partial<Wallet>): Array<Pool> => {
  const keyWithDetails = PoolsCache.keys.all(true, walletData);
  const key = PoolsCache.keys.all(false, walletData);

  const cachedwithDetails =
    queryClient.getQueryData<Array<Pool>>(keyWithDetails);
  if (typeof cachedwithDetails !== "undefined") return cachedwithDetails;

  return queryClient.getQueryData<Array<Pool>>(key);
};

export const onChainFetcher = async (
  walletData: Partial<Wallet>,
  pools: Pool[]
): Promise<Pool[]> => {
  return fetchOnChainPoolsData(walletData, pools);
};

type FetchPoolsResult = {
  pools?: Array<Pool>;
  loading?: boolean;
  loadingOffChain?: boolean;
  loadingOnChain?: boolean;
};

export const useAntePools = <TError = unknown>(
  withDetails: boolean = false,
  walletAddress?: string,
  variables?: GetAntePoolsQueryVariables,
  options?: UseQueryOptions<Pool[], TError, Pool[]>
) => {
  const result = useRef<FetchPoolsResult>({
    pools: undefined,
    loading: undefined,
    loadingOffChain: undefined,
    loadingOnChain: undefined,
  });
  const prevOffChainPools = useRef<Pool[]>();
  const { provider, networkId, account, refreshBalance, isNetworkSupported } =
    useWallet();

  if (typeof walletAddress === "undefined") {
    walletAddress = account;
  }

  const offChainKey = PoolsCache.offChainKeysApiAll() as QueryKey;

  const {
    data: allPoolsRaw,
    isLoading: isLoadingOffChain,
    isFetching,
  } = useQuery(
    offChainKey,
    async () => {
      const raw = await fetchAntePoolsFromAPI();
      return formatPools(raw);
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      ...options,
    }
  );

  const walletData: Partial<Wallet> = {
    networkId,
    provider,
    account: walletAddress,
    refreshBalance,
  };

  const offChainData = useMemo(
    () => handlePoolFilters(allPoolsRaw ?? [], variables ?? {}),
    [allPoolsRaw, variables]
  );

  const key = variables
    ? PoolsCache.keys.filtered(withDetails, walletData, variables)
    : PoolsCache.keys.all(withDetails, walletData);

  const { data: onChainData, isLoading: isLoadingOnChain } = useQuery(
    key,
    () => onChainFetcher(walletData, offChainData),
    {
      enabled:
        isFetching === false &&
        offChainData !== undefined &&
        walletData.provider !== undefined &&
        walletAddress !== undefined &&
        walletData.account !== undefined &&
        walletData.account !== null &&
        isNetworkSupported,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      initialData: () => {
        const cacheWithoutFilters = getCachedPools(walletData);

        const cachedData =
          cacheWithoutFilters && variables
            ? handlePoolFilters(cacheWithoutFilters, variables)
            : cacheWithoutFilters;

        return cachedData;
      },
    }
  );

  if (prevOffChainPools.current !== offChainData) {
    prevOffChainPools.current = offChainData;
    result.current.pools = onChainData ?? offChainData;
  } else {
    result.current.pools = onChainData ?? result.current.pools;
  }

  result.current = {
    ...result.current,
    loading: isLoadingOffChain || isLoadingOnChain,
    loadingOffChain: isLoadingOffChain,
    loadingOnChain: isLoadingOnChain,
  };

  return result.current;
};
